import { Grid, Typography, useTheme } from '@mui/material';
import { EditImage } from "components/MprcImageHandler";
import { ImageUtils } from 'components/MprcImageHandler/ImageResizer';
import { MprcPasteContainer } from "components/MprcPasteContainer";
import { useMprcTheme } from "components/MprcThemeProvider";
import { Formaters } from "lib/Formaters";
import { useSnackbar } from "notistack";
import * as React from 'react';
import { useEffect, useState } from "react";
import Dropzone from 'react-dropzone';
import { useTranslation } from "react-i18next";
import "react-image-crop/dist/ReactCrop.css";
import { ImageSelectionProps } from './';
import { ImageHandlerStyles } from "./ImageHandlerStyles";

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface ILocalImageProps {
    selectedFile: File | null;
    onSelectFile: (image: File | null) => void;
}

type Props = ILocalImageProps & ImageSelectionProps;

function cleanBlob(src: string | null) {
    if (src && src.indexOf("blob") == 0) {
        URL.revokeObjectURL(src);
    }
}

export const LocalImage = (props: Props) => {
    const { classes, cx } = ImageHandlerStyles();
    const { enqueueSnackbar } = useSnackbar();
    const { theme: themeMprc } = useMprcTheme();
    const [src, setSrc] = useState<string | null>(null);
    const { t } = useTranslation("shared");
    const theme = useTheme();
    const { fitMode, acceptedFiles, dimension, allowEdit, maxSize, backgroundColor, selectedFile, onSelectFile } = props;

    ///
    const onLocalFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            if (maxSize && e.target.files[0].size > maxSize) {
                var msg = t("AsyncUpload.MaxSizeExceeded", { fileSize: Formaters.fileSize(e.target.files.length), maxSize: Formaters.fileSize(maxSize) });
                enqueueSnackbar(msg, { variant: "error" });
            }
            else {
                onSelectFile(e.target.files[0]);
            }
        }
    };

    ///
    const handlePaste = (images: File[]) => {
        if (images && images.length > 0) {
            onSelectFile(images[0]);
        }
    };

    ///
    const replaceExtension = (filename: string, ext: string) => {
        var i = filename.lastIndexOf('.');
        if (i > -1) return filename.substr(0, i - 1) + ext;
        return filename;
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (selectedFile == null) {
            cleanBlob(src);
            setSrc(null);
        }
        else {
            ImageUtils.blobToBase64(
                selectedFile,
                allowEdit ? undefined : props.dimension,
                allowEdit ? undefined : props.adjustImageDimensionToRatio
            )
                .then((sourceData) => {

                    setSrc(sourceData);

                    if (!allowEdit) {
                        props.onChange({
                            source: { sourceDataOrUrl: sourceData, name: selectedFile.name, contentType: selectedFile.type },
                            editedImage: null
                        });
                    }
                })
                .catch((e) => {
                    enqueueSnackbar(themeMprc.MprcImageSelection.fileConversionError, { variant: "error" });
                    onSelectFile(null);
                });
        }

        return () => {
            cleanBlob(src);
        };
    }, [selectedFile]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////


    return (
        <>
            <input
                id="ImageSelectionFile"
                type="file"
                accept={acceptedFiles && Object.values(acceptedFiles)[0].join(",")}
                style={{ display: "none" }}
                onChange={onLocalFileSelected}
            />

            <Grid container direction="column" wrap="nowrap" height="100%" spacing={1}>

                <Grid size="grow" height="100%">
                    {
                        src == null
                            ? (
                                <>
                                    <MprcPasteContainer
                                        onPaste={files => handlePaste(files)}
                                        onError={err => console.error(err)}
                                    />
                                    <Dropzone
                                        accept={props.acceptedFiles}
                                        multiple={false}
                                        onDropRejected={
                                            () => enqueueSnackbar(themeMprc.MprcImageSelection.fileNotAccept, { variant: "error" })
                                        }
                                        onDropAccepted={
                                            (e) => {
                                                onSelectFile(e[0]);
                                            }
                                        }
                                    >
                                        {
                                            ({ getRootProps, getInputProps, isDragActive }) => {
                                                return (
                                                    <div
                                                        {...getRootProps({
                                                            style: {
                                                                height: "100%",
                                                                borderWidth: 1,
                                                                borderStyle: "dashed",
                                                                borderColor: isDragActive ? theme.palette.success.main : theme.palette.muted.main
                                                            }
                                                        })}
                                                    >
                                                        <input {...getInputProps()} />

                                                        <Grid container justifyContent="center" height="100%">
                                                            <Grid style={{ alignSelf: "center", textAlign: "center" }}>
                                                                <Typography variant="h2">
                                                                    <p>{themeMprc.MprcImageSelection.dropHereText1}</p>
                                                                    <p>{themeMprc.MprcImageSelection.dropHereText2}</p>
                                                                    <p>{themeMprc.MprcImageSelection.dropHereText3}</p>
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </div>
                                                );
                                            }
                                        }
                                    </Dropzone>
                                </>
                            )
                            : selectedFile && src && !allowEdit
                                ? (
                                    <div className={cx(classes.imageBorder, classes.imageContainer)} >
                                        <img src={src} />
                                    </div>
                                )
                                : selectedFile && src && allowEdit && dimension
                                    ? (

                                        <EditImage
                                            src={src}
                                            fitMode={fitMode}
                                            dimension={dimension}
                                            mimetype={selectedFile.type}
                                            backgroundColor={backgroundColor}
                                            onError={
                                                (message) => {
                                                    onSelectFile(null);
                                                    props.onError(message);
                                                }
                                            }
                                            onChange={
                                                (data) => {
                                                    props.onChange({
                                                        source: { sourceDataOrUrl: src, name: selectedFile.name, contentType: selectedFile.type },
                                                        editedImage: { sourceDataOrUrl: data.dataUrl, name: replaceExtension(selectedFile.name, ".png"), contentType: "image/png" }
                                                    });
                                                }
                                            }
                                        />
                                    )
                                    : null
                    }
                </Grid>
            </Grid>
        </>
    );
};